import { useEffect, useState } from 'react';
import { UserPlus, AlertCircle, CheckCircle2, Save, CalendarClock } from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';
import { customerPortalApi, type PortalSettings } from '../../api/customerPortal';

function toDateInput(iso: string | null): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

export default function SignupApp() {
  const [settings, setSettings] = useState<PortalSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({ signup_enabled: true, signup_message: '', launch_at: '', newsletter_open: true });

  const load = async () => {
    setLoading(true); setError('');
    try {
      const s = await customerPortalApi.getSettings();
      setSettings(s);
      setForm({
        signup_enabled: s.signup_enabled,
        signup_message: s.signup_message || '',
        launch_at: toDateInput(s.launch_at),
        newsletter_open: s.newsletter_open,
      });
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Could not load signup settings.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); }, []);

  const save = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      const s = await customerPortalApi.updateSettings({
        signup_enabled: form.signup_enabled,
        signup_message: form.signup_message || null,
        launch_at: form.launch_at ? new Date(`${form.launch_at}T00:00:00`).toISOString() : null,
        newsletter_open: form.newsletter_open,
      });
      setSettings(s);
      setSuccess('Signup settings saved.');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to save signup settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex h-40 items-center justify-center"><LoadingSpinner size="lg" /></div>;

  return (
    <div className="space-y-6">
      {error && <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2.5 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-400"><AlertCircle size={16} />{error}</div>}
      {success && <div className="flex items-center gap-2 rounded-lg border border-green-300 bg-green-50 px-3 py-2.5 text-sm text-green-700 dark:border-green-800 dark:bg-green-900/30 dark:text-green-400"><CheckCircle2 size={16} />{success}</div>}

      <Card className="space-y-5 p-5">
        <div className="flex items-center gap-2">
          <UserPlus size={18} className="text-slate-500" />
          <div>
            <h2 className="text-base font-semibold text-slate-950 dark:text-slate-100">Public Signup</h2>
            <p className="text-sm text-slate-500">Control whether new users can register, and what visitors see when they cannot.</p>
          </div>
        </div>

        <label className="flex items-center justify-between gap-4 rounded-lg border border-slate-200 p-3 dark:border-slate-800">
          <span className="min-w-0">
            <span className="block text-sm font-medium text-slate-950 dark:text-slate-100">Allow new signups</span>
            <span className="block text-xs text-slate-500">
              {form.signup_enabled ? 'New users can register right now.' : 'Registration is closed; visitors see your message below.'}
            </span>
          </span>
          <input type="checkbox" checked={form.signup_enabled}
            onChange={e => setForm(p => ({ ...p, signup_enabled: e.target.checked }))}
            className="h-5 w-5 shrink-0 rounded border-slate-300 text-brand-600 focus:ring-brand-500" />
        </label>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-800 dark:text-slate-300">Message shown while signup is closed</label>
          <textarea rows={3} value={form.signup_message}
            onChange={e => setForm(p => ({ ...p, signup_message: e.target.value }))}
            placeholder="Our official launch date is November 1. Sign up for the newsletter and we will notify you when we open."
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-slate-800 dark:text-slate-300"><CalendarClock size={14} />Launch date</label>
            <input type="date" value={form.launch_at} onChange={e => setForm(p => ({ ...p, launch_at: e.target.value }))}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
          </div>
          <label className="flex items-end gap-2 pb-2 text-sm text-slate-700 dark:text-slate-300">
            <input type="checkbox" checked={form.newsletter_open}
              onChange={e => setForm(p => ({ ...p, newsletter_open: e.target.checked }))}
              className="h-4 w-4 rounded border-slate-300" />
            Allow newsletter signups while closed
          </label>
        </div>

        <Button onClick={save} disabled={saving}>
          {saving ? <LoadingSpinner size="sm" /> : <Save size={16} />}{saving ? 'Saving...' : 'Save'}
        </Button>
      </Card>

      <Card className="p-5">
        <h3 className="mb-2 text-sm font-semibold text-slate-950 dark:text-slate-100">Preview — what visitors see</h3>
        <div className="rounded-lg border border-dashed border-slate-300 p-4 dark:border-slate-700">
          {form.signup_enabled ? (
            <p className="text-sm text-green-600 dark:text-green-400">Registration is open — this message is hidden.</p>
          ) : (
            <>
              <p className="text-sm text-slate-700 dark:text-slate-300">{form.signup_message || 'Our official launch date is November 1. Sign up for the newsletter and we will notify you when we open.'}</p>
              {form.newsletter_open && <p className="mt-2 text-xs text-brand-600 dark:text-brand-400">A newsletter signup field is shown.</p>}
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
